'use client'

import { useState } from 'react'
import { fulfillmentApi, type FulfillmentApi } from '@entities/fulfillment'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'
import { Checkbox } from '@ui/base/checkbox'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
} from '@ui/base/combobox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Label } from '@ui/base/label'
import { typeClass } from '@utils/typography'

import { maxRequestCityCount } from '../config'
import { useDirectorySearch } from '../lib/use-directory-search'
import { type WizardCity } from '../model/types'

const optionLabel = (option: FulfillmentApi.ServiceCity) => `${option.name}, ${option.region_name}`

const isSame = (left: WizardCity, right: WizardCity) =>
  left.name.toLocaleLowerCase('ru') === right.name.toLocaleLowerCase('ru') &&
  left.regionName.toLocaleLowerCase('ru') === right.regionName.toLocaleLowerCase('ru')

const searchCities = (query: string) =>
  fulfillmentApi.serviceCityOptions({ q: query, limit: 20 }).then((response) => response.data.data?.cities ?? [])

/** Города, откуда фулфилмент забирает товар. Ни одного города — «город неважен». */
export const CityPicker = ({
  cities,
  onChange,
}: {
  cities: WizardCity[]
  onChange: (cities: WizardCity[]) => void
}) => {
  const [query, setQuery] = useState('')
  const [isListOpen, setIsListOpen] = useState(false)
  const [manualRegion, setManualRegion] = useState('')
  const { options, isFailed } = useDirectorySearch(query, searchCities)
  const isLimitReached = cities.length >= maxRequestCityCount

  const addCity = (city: WizardCity) => {
    // Предел держится не только на disabled у полей выше: любая новая точка добавления обошла бы
    // его молча, а перебор всплыл бы уже отказом бэкенда.
    if (isLimitReached) {
      return
    }
    if (!cities.some((selected) => isSame(selected, city))) {
      onChange([...cities, city])
    }
    setQuery('')
    setManualRegion('')
    setIsListOpen(false)
  }

  const togglePriority = (city: WizardCity) =>
    onChange(cities.map((item) => (isSame(item, city) ? { ...item, isPriority: !item.isPriority } : item)))

  return (
    <div className="grid gap-3">
      <Field data-invalid={isFailed}>
        <FieldLabel htmlFor="request-city-search">Город, откуда забирать товар</FieldLabel>
        <Combobox
          items={options}
          filter={null}
          value={null}
          onValueChange={(option) => {
            if (option) {
              addCity({ name: option.name, regionName: option.region_name, isPriority: false })
            }
          }}
          inputValue={query}
          onInputValueChange={(value, details) => {
            if (details.reason !== 'input-change') {
              return
            }
            setQuery(value)
            setIsListOpen(Boolean(value.trim()))
          }}
          itemToStringLabel={optionLabel}
          open={isListOpen && options.length > 0}
          onOpenChange={setIsListOpen}
          openOnInputClick={false}
        >
          <ComboboxInput id="request-city-search" placeholder="Например, Москва" disabled={isLimitReached} />
          <ComboboxPositioner>
            <ComboboxPopup>
              <ComboboxEmpty>Город не найден</ComboboxEmpty>
              <ComboboxList>
                {options.map((option) => (
                  <ComboboxItem key={option.id} value={option}>
                    <ComboboxItemIndicator />
                    {optionLabel(option)}
                  </ComboboxItem>
                ))}
              </ComboboxList>
            </ComboboxPopup>
          </ComboboxPositioner>
        </Combobox>
        <FieldError>{isFailed ? 'Справочник городов недоступен. Добавьте город и регион вручную.' : ''}</FieldError>
      </Field>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Field className="flex-1">
          <FieldLabel htmlFor="request-city-region">Регион, если города нет в списке</FieldLabel>
          <Input
            id="request-city-region"
            value={manualRegion}
            placeholder="Например, Московская область"
            disabled={isLimitReached}
            onChange={(event) => setManualRegion(event.target.value)}
          />
        </Field>
        <Button
          type="button"
          variant="outline"
          disabled={isLimitReached || !query.trim() || !manualRegion.trim()}
          onClick={() => addCity({ name: query.trim(), regionName: manualRegion.trim(), isPriority: false })}
        >
          Добавить город вручную
        </Button>
      </div>

      {cities.length === 0 && (
        <span className={`${typeClass.supporting} text-muted-foreground`}>
          Город не выбран — заявку увидят фулфилменты из любого города.
        </span>
      )}
      {isLimitReached && (
        <span className={`${typeClass.supporting} text-muted-foreground`}>
          Больше {maxRequestCityCount} городов в одну заявку не поместится. Чтобы добавить другой, сначала уберите
          ненужные.
        </span>
      )}
      {cities.length > 0 && (
        <div className="grid gap-2" aria-label="Выбранные города">
          {cities.map((city, index) => {
            // Номер строки, а не название: у городов-тёзок из разных регионов совпали бы
            // идентификаторы флажков, и клик по подписи переключал бы чужой флажок.
            const priorityId = `request-city-priority-${index}`

            return (
              <div key={`${city.name}-${city.regionName}`} className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">
                  {city.name}, {city.regionName}
                </Badge>
                <Label htmlFor={priorityId} className="cursor-pointer">
                  <Checkbox
                    id={priorityId}
                    aria-label={`Важный город: ${city.name}, ${city.regionName}`}
                    checked={city.isPriority}
                    onCheckedChange={() => togglePriority(city)}
                  />
                  <span className={typeClass.supporting}>Важный город</span>
                </Label>
                <Button
                  type="button"
                  aria-label={`Убрать ${city.name}, ${city.regionName}`}
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => onChange(cities.filter((item) => !isSame(item, city)))}
                >
                  ×
                </Button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
